<template>
    <div>
        <b-row>
            <b-col cols="6">
                <BaseTextInput v-model="firstName"
                               :v="$v.firstName"
                               label="First Name"
                               slug="firstName"
                               :plaintext = "searchedCNA"
                />
            </b-col>
            <b-col cols="6">
                <BaseTextInput v-model="lastName"
                               :v="$v.lastName"
                               label="Last Name"
                               slug="lastName"
                               :plaintext = "searchedCNA"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="12">
                <BaseMaskedTextInput v-model="ssn"
                                     :v="$v.ssn"
                                     slug="ssn"
                                     label="Social Security Number"
                                     setId="ssn"
                                     mask="###-##-####"
                                     tip="9 digits"
                                     :plaintext = "searchedCNA"
                />
            </b-col>
        </b-row>
        <b-row v-if="nameChange">
            <b-col>
                <div class="text-muted">CNA's name has changed.</div>
            </b-col>
        </b-row>
      <b-row v-if="inactive">
        <b-col>
          <div class="text-danger">
            Nurse Aides with an inactive registry status cannot work as a nurse aide in nursing homes or certified boarding care homes until their status is active. Unless the individual can prove that he or she has recently successfully completed a training and competency evaluation program approved by the State and has not yet been included in the registry. Facilities must follow up to ensure that such an individual actually becomes registered (<a href="https://www.ecfr.gov/current/title-42/part-483/section-483.35#p-483.35(d)(4)(ii)">42 CFR 483.35(d)(4)(ii)</a>). Please communicate directly with the nurse aide to resolve and/or how to update their status. Resources can be found on the <a href="https://www.health.state.mn.us/facilities/providers/nursingassistant/index.html">Nurse Aide Registry website</a>.
          </div>
        </b-col>
      </b-row>
      <b-row v-if="notFound">
        <b-col>
          <div class="text-danger">Nurse Aides not found in the registry cannot be employed as nurse aides until they are registered and active. Please communicate directly with the nurse aide to resolve their status.<br/>
            The Nurse Aide may have a social security discrepancy which requires verification by the provider and/or the nurse aide. If the nurse aide has an incorrect SSN in the registry, a correction can be submitted through the registry by the Nurse Aide. If the provider made a data entry error, re-submit with the corrected SSN.<br/>
            Instructions can be found at <a href="https://www.health.state.mn.us/facilities/providers/nursingassistant/resources.html" target="_blank">Nursing Assistant Resources</a>.</div>
        </b-col>
      </b-row>
      <b-row v-if="searchError">
        <b-col>
          <div class="text-danger">An error occurred during search.</div>
        </b-col>
      </b-row>
        <b-row>
            <b-col>
                <b-button v-if="searchedCNA || notFound || searchError" @click="clearSearch" variant="secondary" class="m-2">Clear</b-button>
                <b-button v-else @click="searchCNA" variant="primary" class="m-2">Search</b-button>
            </b-col>
        </b-row>
        <div v-if="searchedCNA && !inactive">
            <b-row>
                <b-col>
                    <BaseSelector v-model="status"
                                  :v="$v.status"
                                  label="Status"
                                  slug="status"
                                  :options="statusOptions"
                    />
                </b-col>
            </b-row>
            <b-row v-if="status==='OTH'">
                <b-col>
                    <BaseTextInput v-model="otherStatus"
                                   :v="$v.otherStatus"
                                   label="Other Status"
                                   slug="othStatus"/>
                </b-col>
            </b-row>
            <b-row v-if="status==='ONC' || status==='LOA' || status==='TER' || status==='OTH' || status==='CUR'">
                <b-col>
                    <BaseDateOfBirthInput v-model="lastWorkedDate"
                                   :v="$v.lastWorkedDate"
                                   label="Last Worked Date"
                                   slug="lastWorkedDate"
                                   tip="Format: MM/DD/YYYY"
                    />
                </b-col>
            </b-row>
        </div>
        <b-row>
            <b-col>
                <b-button variant="primary" v-if="searchedCNA && !inactive" @click="addCNA" class="m-2">Submit</b-button>
            </b-col>
            <b-col>
                <b-button variant="secondary" @click="closeModal" class="m-2">Cancel</b-button>
            </b-col>
        </b-row>
    </div>
</template>

<script>

    import {required, minLength, requiredIf} from 'vuelidate/lib/validators'
    import BaseTextInput from "@/components/templates/input/BaseTextInput";
    import BaseMaskedTextInput from "@/components/templates/input/BaseMaskedTextInput";
    import axios from 'axios';
    import BaseSelector from "@/components/templates/input/BaseSelector";
    import BaseDateOfBirthInput from "@/components/templates/input/BaseDateOfBirthInput";

    export default {
        name: "ProviderCNAAdd",
        components:{
            BaseTextInput,
            BaseMaskedTextInput,
            BaseSelector,
            BaseDateOfBirthInput
        },
        props:{
            statusOptions: Array
        },
        data(){
            return {
                firstName: null,
                lastName: null,
                ssn: null,
                searchedCNA: null,
                nameChange: null,
                status: null,
                otherStatus: null,
                lastWorkedDate: null,
                inactive: null,
                notFound: null,
                searchError: null
            }
        },
        validations:{
            firstName: {required},
            lastName: {required},
            ssn: {required, minLength:minLength(11)},
            status: {required: requiredIf(function(){
                    return this.searchedCNA;
                })},
            otherStatus: {
                required: requiredIf(function(){
                    return this.status==='OTH';
                })
            },
            lastWorkedDate: {required: requiredIf(function(){
                    return this.searchedCNA && (this.status==='ONC' || this.status==='LOA' || this.status==='TER' || this.status==='OTH' || this.status==='CUR')
                })}
        },
        methods:{
            searchCNA(){
                this.$v.$touch();
                if(this.$v.$invalid){
                    return;
                }
                this.inactive = false;
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/provider/cna/search`,{
                    firstName: this.firstName,
                    lastName: this.lastName,
                    ssn: this.ssn
                })
                    .then(response=>{
                        this.searchedCNA = response.data;

                        this.$nextTick(()=>{
                            if(this.searchedCNA.firstName.toLowerCase() !== this.firstName.toLowerCase()){
                                this.nameChange = true;
                                this.firstName = this.searchedCNA.firstName;
                            }
                            if(this.searchedCNA.lastName.toLowerCase() !== this.lastName.toLowerCase()){
                                this.nameChange = true;
                                this.lastName = this.searchedCNA.lastName;
                            }
                            this.inactive = !this.searchedCNA.active;
                        })
                    })
                    .catch(error=>{
                      if(error.response.status===404) {
                        this.notFound = true;
                      }
                      else{
                        this.searchError=true;
                      }
                    })
            },
            addCNA(){
                this.$v.$touch();
                if(this.$v.$invalid){
                    return;
                }
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/provider/cna/add`,{
                    id: this.searchedCNA.id,
                    status: this.status,
                    otherStatus: this.otherStatus,
                    lastWorkedDate: this.lastWorkedDate
                }).then(response=>{
                    this.$emit('success');
                    this.$emit('hideAddModal');
                }).catch(error=>{
                    this.$emit('failure');
                })
            },
            clearSearch(){
                this.searchedCNA = null;
                this.firstName = '';
                this.lastName = '';
                this.ssn='';
                this.nameChange=false;
                this.inactive=false;
                this.notFound=false;
                this.searchError=false;
            },
            closeModal(){
                this.$emit('hideAddModal');
            }
        }
    }
</script>

<style scoped>

</style>